// src/components/shared/contexts/ConfirmContext.js
import React, { createContext, useState, useContext, useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FiAlertTriangle, FiX } from 'react-icons/fi';

const ConfirmContext = createContext(null);

export const useConfirmDialog = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirmDialog must be used within a ConfirmProvider');
  }
  return context;
};

function ConfirmDialog({ config, onResult }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!config) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onResult(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [config, onResult]);

  useEffect(() => {
    if (!config || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll('button');
    if (focusable.length) focusable[focusable.length - 1].focus();
  }, [config]);

  if (!config) return null;

  const { title = 'Confirm', message, confirmLabel = 'Confirm', variant = 'default' } = config;
  const isDanger = variant === 'danger';

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm" aria-hidden="true" onClick={() => onResult(false)} />
        <div ref={dialogRef} className="relative inline-block bg-warm-gray/95 backdrop-blur-xl border border-white/10 rounded-2xl text-left overflow-hidden shadow-modal transform transition-all sm:max-w-md w-full animate-fade-in-up">
          <div className="px-6 pt-6 pb-2">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                {isDanger && (
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-soft-coral/10 flex items-center justify-center">
                    <FiAlertTriangle className="h-5 w-5 text-soft-coral" />
                  </div>
                )}
                <h3 className="text-lg font-bold text-cream" id="confirm-title">{title}</h3>
              </div>
              <button
                type="button"
                className="p-1 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                onClick={() => onResult(false)}
              >
                <span className="sr-only">Close</span>
                <FiX className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="px-6 py-4">
            <p className="text-sm text-cream/70 whitespace-pre-line">{message}</p>
          </div>
          <div className="px-6 pb-6 flex justify-end gap-3">
            <button
              type="button"
              className="btn btn-secondary px-5 py-2.5 text-xs"
              onClick={() => onResult(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`btn px-5 py-2.5 text-xs ${isDanger ? 'btn-danger' : 'btn-primary'}`}
              onClick={() => onResult(true)}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ConfirmProvider({ children }) {
  const [config, setConfig] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setConfig(options);
    });
  }, []);

  const handleResult = useCallback((result) => {
    setConfig(null);
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
  }, []);

  const value = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmDialog config={config} onResult={handleResult} />
    </ConfirmContext.Provider>
  );
}

ConfirmProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
